import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Plus, Link2, ArrowLeft,
  CheckCircle2, Clock, AlertCircle, XCircle,
  ExternalLink, Trash2, Settings,
  Loader2, Copy, Database, Lock, Unlock,
  ChevronDown, ChevronUp, Edit3, Save, X, Send,
  Rocket, Unplug,
} from 'lucide-react';
import DomainSearchModal from '@/components/DomainSearchModal';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active:      { icon: CheckCircle2, color: 'var(--orbis-positive)',  bg: 'rgba(52,211,153,0.1)',  label: 'Active' },
  pending:     { icon: Clock,        color: 'var(--orbis-amber)',     bg: 'rgba(212,184,150,0.1)', label: 'Pending' },
  dns_pending: { icon: Settings,     color: '#60A5FA',               bg: 'rgba(96,165,250,0.1)',  label: 'DNS Setup' },
  expired:     { icon: XCircle,      color: 'var(--orbis-t4)',        bg: 'rgba(255,255,255,0.05)', label: 'Expired' },
  failed:      { icon: AlertCircle,  color: '#EF4444',               bg: 'rgba(239,68,68,0.1)',   label: 'Failed' },
  canceled:    { icon: XCircle,      color: 'var(--orbis-t4)',        bg: 'rgba(255,255,255,0.05)', label: 'Canceled' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color }}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ── DNS instructions panel ────────────────────────────────────────────────────

function DnsInstructionsPanel({ domain, onDone }) {
  const SITES_HOST = 'sites.orbis.build';
  const [marking, setMarking] = useState(false);

  const handleDone = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/api/domains/${domain}/configure-dns`);
      toast.success('DNS marked as configured');
      if (onDone) onDone();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--orbis-amber)' }} />
        <p style={{ fontSize: 12, color: 'var(--orbis-t2)', lineHeight: 1.6 }}>
          Add this DNS record at your domain registrar to point <strong style={{ color: 'var(--orbis-t1)' }}>{domain}</strong> to Orbis:
        </p>
      </div>
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--orbis-s3)' }}>
        <div className="grid text-xs" style={{ gridTemplateColumns: '60px 80px 1fr' }}>
          {['Type', 'Name', 'Value'].map(h => (
            <div key={h} className="px-3 py-2" style={{ color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'var(--orbis-s1)', borderBottom: '1px solid var(--orbis-s3)' }}>{h}</div>
          ))}
          <div className="px-3 py-2.5" style={{ color: 'var(--orbis-blue)', fontFamily: 'var(--orbis-mono)' }}>CNAME</div>
          <div className="px-3 py-2.5" style={{ color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>www</div>
          <div className="px-3 py-2.5 flex items-center gap-1" style={{ color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
            <span className="truncate">{SITES_HOST}</span>
            <button onClick={() => { navigator.clipboard.writeText(SITES_HOST); toast.success('Copied'); }}
              className="rounded p-1 hover:bg-[var(--orbis-s2)] transition-colors shrink-0" style={{ color: 'var(--orbis-t4)' }}>
              <Copy className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--orbis-t4)', lineHeight: 1.5 }}>
        DNS changes take up to 48 hours to propagate. Once done, click below.
      </p>
      <button onClick={handleDone} disabled={marking}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors"
        style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t1)', border: '1px solid var(--orbis-s3)', opacity: marking ? 0.5 : 1, cursor: marking ? 'not-allowed' : 'pointer' }}>
        {marking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        I've added the DNS record
      </button>
    </div>
  );
}

// ── Connect existing domain form ──────────────────────────────────────────────

function ConnectDomainForm({ projects, onConnected }) {
  const [domain, setDomain] = useState('');
  const [projectId, setProjectId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [result, setResult] = useState(null);

  const handleConnect = async () => {
    if (!domain || !projectId) return;
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/api/deploy/custom-domain`, {
        project_id: projectId,
        domain,
      });
      setResult(data);
      toast.success('Domain connected — add the DNS record below');
      if (onConnected) onConnected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to connect domain');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
      <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--orbis-t1)' }}>Connect an existing domain</h3>
      <p style={{ fontSize: 12, color: 'var(--orbis-t3)', lineHeight: 1.6 }}>
        Already own a domain? Connect it to one of your published projects.
      </p>
      <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
        <input type="text" value={domain} onChange={e => setDomain(e.target.value)}
          placeholder="www.yourdomain.com"
          className="h-10 rounded-lg px-3 outline-none"
          style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)', fontSize: 13, color: 'var(--orbis-t1)' }} />
        <select value={projectId} onChange={e => setProjectId(e.target.value)}
          className="h-10 rounded-lg px-3 outline-none"
          style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)', fontSize: 13, color: projectId ? 'var(--orbis-t1)' : 'var(--orbis-t4)' }}>
          <option value="">Select project…</option>
          {projects.filter(p => p.status === 'deployed').map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button onClick={handleConnect} disabled={!domain || !projectId || connecting}
          className="h-10 px-4 rounded-lg text-xs font-medium transition-colors"
          style={{ background: 'var(--orbis-blue)', color: 'var(--orbis-bg)', opacity: (!domain || !projectId || connecting) ? 0.4 : 1, cursor: (!domain || !projectId || connecting) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
          {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect'}
        </button>
      </div>
      {result && (
        <DnsInstructionsPanel domain={domain} onDone={() => setResult(null)} />
      )}
    </div>
  );
}

// ── DNS Manager panel ─────────────────────────────────────────────────────────

const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'URL', 'URL301'];

function DnsManagerPanel({ domain, onClose }) {
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [edits, setEdits]       = useState([]);    // working copy

  const newRecord = () => ({ hostname: '', record_type: 'A', address: '', ttl: 1800, mx_pref: 10 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/domains/${domain}/dns`);
        setRecords(data);
        setEdits(data.map(r => ({ ...r })));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load DNS records');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [domain]);

  const updateEdit = (i, field, val) => {
    setEdits(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };

  const removeEdit = (i) => setEdits(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const valid = edits.filter(r => r.hostname.trim() && r.address.trim());
    if (!valid.length) { toast.error('At least one complete record is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/domains/${domain}/dns`, valid);
      setRecords(valid);
      toast.success(`DNS records saved (${valid.length} records)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save DNS records');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg space-y-3 p-4" style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t1)' }}>DNS Records</span>
        </div>
        <button onClick={onClose} className="rounded p-1 hover:bg-[var(--orbis-s2)]" style={{ color: 'var(--orbis-t4)' }}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ background: 'rgba(212,184,150,0.08)', border: '1px solid rgba(212,184,150,0.2)' }}>
        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: 'var(--orbis-amber)' }} />
        <p style={{ fontSize: 11, color: 'var(--orbis-t3)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--orbis-amber)' }}>Full replace:</strong> Saving overwrites all DNS records. Records not listed here will be deleted.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--orbis-t4)' }} />
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="grid text-xs gap-2" style={{ gridTemplateColumns: '80px 100px 1fr 70px 24px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingLeft: 8, paddingRight: 8 }}>
            <span>Type</span><span>Host</span><span>Value</span><span>TTL</span><span></span>
          </div>

          {edits.map((rec, i) => (
            <div key={i} className="grid items-center gap-2 rounded-lg px-2 py-1.5" style={{ gridTemplateColumns: '80px 100px 1fr 70px 24px', background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border)' }}>
              <select
                value={rec.record_type}
                onChange={e => updateEdit(i, 'record_type', e.target.value)}
                className="rounded px-1.5 py-1 outline-none text-xs"
                style={{ background: 'var(--orbis-s2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
                {DNS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                value={rec.hostname}
                onChange={e => updateEdit(i, 'hostname', e.target.value)}
                placeholder="@"
                className="rounded px-2 py-1 outline-none text-xs"
                style={{ background: 'var(--orbis-s2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }} />
              <input
                value={rec.address}
                onChange={e => updateEdit(i, 'address', e.target.value)}
                placeholder="IP or hostname"
                className="rounded px-2 py-1 outline-none text-xs"
                style={{ background: 'var(--orbis-s2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }} />
              <input
                type="number"
                value={rec.ttl}
                onChange={e => updateEdit(i, 'ttl', parseInt(e.target.value) || 1800)}
                className="rounded px-2 py-1 outline-none text-xs"
                style={{ background: 'var(--orbis-s2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }} />
              <button onClick={() => removeEdit(i)} className="flex items-center justify-center w-5 h-5 rounded hover:bg-[rgba(239,68,68,0.15)]" style={{ color: 'var(--orbis-t4)' }}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}

          {/* Add record + Save */}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setEdits(prev => [...prev, newRecord()])}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors hover:bg-[var(--orbis-s2)]"
              style={{ color: 'var(--orbis-t3)', border: '1px dashed var(--orbis-s3)' }}>
              <Plus className="w-3 h-3" />
              Add Record
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg font-medium transition-colors"
              style={{ background: 'var(--orbis-blue)', color: 'var(--orbis-bg)', opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save DNS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transfer out panel ────────────────────────────────────────────────────────

function TransferOutPanel({ domain, onClose, onRemove }) {
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked]   = useState(false);
  const [instructions, setInstructions] = useState('');

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API}/api/domains/${domain}/unlock`);
      setUnlocked(data.unlocked);
      setInstructions(data.instructions || '');
      toast.success('Domain unlocked for transfer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unlock domain');
    } finally {
      setUnlocking(false);
    }
  };

  const steps = [
    {
      num: 1,
      title: 'Unlock your domain',
      content: unlocked ? (
        <p className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--orbis-positive)' }}>
          <CheckCircle2 className="w-4 h-4 shrink-0" /> Domain unlocked. Transfer lock removed.
        </p>
      ) : (
        <button
          onClick={handleUnlock}
          disabled={unlocking}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t1)', border: '1px solid var(--orbis-s3)', opacity: unlocking ? 0.5 : 1, cursor: unlocking ? 'not-allowed' : 'pointer' }}>
          {unlocking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
          Unlock Domain
        </button>
      ),
    },
    {
      num: 2,
      title: 'Get your EPP / Auth code',
      content: (
        <p style={{ fontSize: 12, color: 'var(--orbis-t3)', lineHeight: 1.6 }}>
          Namecheap will email the EPP code to <strong style={{ color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-mono)' }}>domains@orbis.build</strong> — our registrant address.
          Contact us and we'll forward it to you.{' '}
          <a href="mailto:support@orbis.build" style={{ color: 'var(--orbis-blue)', textDecoration: 'underline' }}>
            support@orbis.build
          </a>
        </p>
      ),
    },
    {
      num: 3,
      title: 'Initiate transfer at your new registrar',
      content: (
        <p style={{ fontSize: 12, color: 'var(--orbis-t3)', lineHeight: 1.6 }}>
          Enter the EPP code at your new registrar when prompted. The transfer takes <strong style={{ color: 'var(--orbis-t2)' }}>5–7 business days</strong> to complete.
        </p>
      ),
    },
    {
      num: 4,
      title: 'After transfer completes',
      content: (
        <div className="space-y-2">
          <p style={{ fontSize: 12, color: 'var(--orbis-t3)', lineHeight: 1.6 }}>
            Once transferred, the domain will no longer appear in Orbis. Remove it from your portfolio:
          </p>
          <button
            onClick={onRemove}
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition-colors hover:bg-[rgba(239,68,68,0.15)]"
            style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}>
            <Trash2 className="w-3 h-3" />
            Remove from portfolio
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-3 rounded-lg p-4 space-y-4" style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Send className="w-3.5 h-3.5" style={{ color: 'var(--orbis-amber)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t1)' }}>Transfer Domain Out</span>
        </div>
        <button onClick={onClose} className="rounded p-1 hover:bg-[var(--orbis-s2)]" style={{ color: 'var(--orbis-t4)' }}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {steps.map(step => (
        <div key={step.num} className="flex gap-3">
          <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5"
            style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t3)', border: '1px solid var(--orbis-s3)' }}>
            {step.num}
          </div>
          <div className="space-y-2 flex-1 min-w-0">
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t1)' }}>{step.title}</p>
            {step.content}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Domain row ────────────────────────────────────────────────────────────────

function DomainRow({ domain: d, projects, onRefresh }) {
  const [showDns, setShowDns]           = useState(false);
  const [showDnsManager, setShowDnsManager] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [assigning, setAssigning]       = useState(false);
  const [removing, setRemoving]         = useState(false);

  const assignedProject = projects.find(p => p.id === d.project_id);

  const handleAssign = async (projectId) => {
    if (!projectId) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/api/domains/${d.domain}/assign`, { project_id: projectId });
      toast.success('Domain assigned');
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${d.domain} from your portfolio?`)) return;
    setRemoving(true);
    try {
      await axios.delete(`${API}/api/domains/${d.domain}`);
      toast.success(`${d.domain} removed`);
      onRefresh();
    } catch {
      toast.error('Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
      <div className="grid items-center gap-4 px-5 py-4" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr auto' }}>
        {/* Domain */}
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-t3)' }} />
          <div className="min-w-0">
            <a href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
              {d.domain}
              <ExternalLink className="w-3 h-3 shrink-0" style={{ color: 'var(--orbis-t4)' }} />
            </a>
            {d.expiry_date && (
              <p style={{ fontSize: 11, color: 'var(--orbis-t4)', marginTop: 1 }}>
                Expires {new Date(d.expiry_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* Status */}
        <StatusBadge status={d.status} />

        {/* Project */}
        <div>
          {assigning ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--orbis-t4)' }} />
          ) : (
            <select
              value={d.project_id || ''}
              onChange={e => handleAssign(e.target.value)}
              className="text-xs rounded-md px-2 py-1.5 outline-none w-full"
              style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)', color: d.project_id ? 'var(--orbis-t1)' : 'var(--orbis-t4)' }}>
              <option value="">Unassigned</option>
              {projects.filter(p => p.status === 'deployed').map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Auto-renew */}
        <div>
          <span className="text-xs px-2 py-1 rounded" style={{ background: d.auto_renew ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.04)', color: d.auto_renew ? 'var(--orbis-positive)' : 'var(--orbis-t4)' }}>
            {d.auto_renew ? '✓ Auto-renew' : 'Manual'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {(d.status === 'dns_pending' || !d.dns_configured) && d.status === 'active' && (
            <button onClick={() => setShowDns(v => !v)}
              className="rounded-lg p-2 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{ color: 'var(--orbis-amber)' }} title="DNS Setup">
              <Settings className="w-3.5 h-3.5" />
            </button>
          )}
          {d.status === 'active' && (
            <button
              onClick={() => { setShowDnsManager(v => !v); setShowTransfer(false); }}
              className="rounded-lg p-2 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{ color: showDnsManager ? 'var(--orbis-blue)' : 'var(--orbis-t4)' }}
              title="Manage DNS">
              <Database className="w-3.5 h-3.5" />
            </button>
          )}
          {d.status === 'active' && (
            <button
              onClick={() => { setShowTransfer(v => !v); setShowDnsManager(false); }}
              className="rounded-lg p-2 transition-colors hover:bg-[var(--orbis-s2)]"
              style={{ color: showTransfer ? 'var(--orbis-amber)' : 'var(--orbis-t4)' }}
              title="Transfer Out">
              <Send className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={handleRemove} disabled={removing}
            className="rounded-lg p-2 transition-colors hover:bg-[rgba(239,68,68,0.1)]"
            style={{ color: 'var(--orbis-t4)', cursor: removing ? 'not-allowed' : 'pointer' }} title="Remove">
            {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* DNS setup (for external / not-yet-configured domains) */}
      {showDns && (
        <div className="px-5 pb-4">
          <DnsInstructionsPanel domain={d.domain} onDone={() => { setShowDns(false); onRefresh(); }} />
        </div>
      )}

      {/* DNS Manager */}
      {showDnsManager && (
        <div className="px-5 pb-4">
          <DnsManagerPanel domain={d.domain} onClose={() => setShowDnsManager(false)} />
        </div>
      )}

      {/* Transfer Out */}
      {showTransfer && (
        <div className="px-5 pb-4">
          <TransferOutPanel
            domain={d.domain}
            onClose={() => setShowTransfer(false)}
            onRemove={handleRemove}
          />
        </div>
      )}
    </div>
  );
}

// ── Active Sites (deployed .orbis.build subdomains) ─────────────────────────

function ActiveSitesSection({ projects, onRefresh }) {
  const [unpublishing, setUnpublishing] = useState(null);

  const deployedProjects = (projects || []).filter(p => p.subdomain_slug);

  const handleUnpublish = async (projectId, slug) => {
    if (!window.confirm(`Unpublish ${slug}.orbis.build? This will take the site offline and free the subdomain.`)) return;
    setUnpublishing(projectId);
    try {
      await axios.post(`${API}/api/deploy/unpublish`, { project_id: projectId });
      toast.success(`${slug}.orbis.build unpublished`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    } finally {
      setUnpublishing(null);
    }
  };

  if (deployedProjects.length === 0) return null;

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="w-4 h-4" style={{ color: 'var(--orbis-positive)' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--orbis-t2)', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'var(--orbis-mono)' }}>
          Active Sites
        </span>
        <span style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
          {deployedProjects.length}
        </span>
      </div>

      <div className="space-y-2">
        {deployedProjects.map(p => {
          const hostname = `${p.subdomain_slug}.orbis.build`;
          const liveUrl = `https://${hostname}`;
          return (
            <div key={p.id} className="flex items-center justify-between px-5 py-3.5 rounded-xl transition-colors"
              style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-border-md)' }}>

              {/* Left: site info */}
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)' }}>
                  <Globe className="w-4 h-4" style={{ color: 'var(--orbis-positive)' }} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <a href={liveUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 transition-opacity hover:opacity-70"
                      style={{ fontSize: 14, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
                      {hostname}
                      <ExternalLink className="w-3 h-3" style={{ color: 'var(--orbis-t4)' }} />
                    </a>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                      style={{ background: 'rgba(52,211,153,0.1)', color: 'var(--orbis-positive)', fontSize: 10, fontWeight: 600 }}>
                      <CheckCircle2 className="w-2.5 h-2.5" /> Live
                    </span>
                  </div>
                  <p className="truncate" style={{ fontSize: 12, color: 'var(--orbis-t4)', marginTop: 2 }}>
                    {p.name}
                    {p.custom_domain && (
                      <span style={{ color: 'var(--orbis-t3)', marginLeft: 8 }}>+ {p.custom_domain}</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Right: actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(liveUrl);
                    toast.success('URL copied');
                  }}
                  className="p-2 rounded-lg transition-colors hover:opacity-70"
                  style={{ color: 'var(--orbis-t3)' }}
                  title="Copy URL"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleUnpublish(p.id, p.subdomain_slug)}
                  disabled={unpublishing === p.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:opacity-80"
                  style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}
                  title="Unpublish site"
                >
                  {unpublishing === p.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Unplug className="w-3 h-3" />
                  )}
                  Unpublish
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Subtle divider */}
      <div className="mt-6 mb-2" style={{ borderTop: '0.5px solid var(--orbis-border)' }} />
    </div>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────

export default function DomainsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [domains, setDomains]           = useState([]);
  const [projects, setProjects]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showSearch, setShowSearch]     = useState(false);
  const [showConnect, setShowConnect]   = useState(false);

  const load = useCallback(async () => {
    try {
      const [domRes, projRes] = await Promise.all([
        axios.get(`${API}/api/domains`),
        axios.get(`${API}/api/projects`),
      ]);
      setDomains(domRes.data);
      setProjects(projRes.data);
    } catch {
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle redirect back from Stripe
  useEffect(() => {
    const registered = searchParams.get('registered');
    if (registered) {
      toast.success(`${registered} registered! It may take a few minutes to activate.`);
    }
  }, [searchParams]);

  // Poll while any domain is pending
  useEffect(() => {
    const hasPending = domains.some(d => d.status === 'pending');
    if (!hasPending) return;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [domains, load]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--orbis-bg)' }}>

      {/* Nav bar */}
      <nav className="flex items-center justify-between px-6" style={{ height: 64, borderBottom: '0.5px solid var(--orbis-border)', background: 'var(--orbis-bg)' }}>
        <button onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 transition-opacity hover:opacity-70"
          style={{ color: 'var(--orbis-t3)', fontSize: 13 }}>
          <ArrowLeft className="w-4 h-4" />
          Dashboard
        </button>
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4" style={{ color: 'var(--orbis-blue)' }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--orbis-t1)' }}>Domain Portfolio</span>
        </div>
        <div style={{ width: 100 }} />
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 300, color: 'var(--orbis-t1)', marginBottom: 4 }}>Domain Portfolio</h1>
            <p style={{ fontSize: 13, color: 'var(--orbis-t3)' }}>
              {projects.filter(p => p.subdomain_slug).length} active site{projects.filter(p => p.subdomain_slug).length !== 1 ? 's' : ''} · {domains.length} domain{domains.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowConnect(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors"
              style={{ background: 'var(--orbis-s1)', color: 'var(--orbis-t2)', border: '1px solid var(--orbis-border-md)' }}>
              <Link2 className="w-4 h-4" />
              Connect Existing
            </button>
            <button onClick={() => setShowSearch(true)}
              className="orbis-cta flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-all">
              <Plus className="w-4 h-4" />
              Register a Domain
            </button>
          </div>
        </div>

        {/* Connect existing form */}
        {showConnect && (
          <ConnectDomainForm projects={projects} onConnected={() => { load(); setShowConnect(false); }} />
        )}

        {/* Active Sites — deployed .orbis.build subdomains */}
        <ActiveSitesSection projects={projects} onRefresh={load} />

        {/* Domain list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--orbis-t4)' }} />
          </div>
        ) : domains.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-xl space-y-4"
            style={{ background: 'var(--orbis-s1)', border: '1px dashed var(--orbis-border-md)' }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(184,204,223,0.08)', border: '1px solid rgba(184,204,223,0.15)' }}>
              <Globe className="w-6 h-6" style={{ color: 'var(--orbis-blue)' }} />
            </div>
            <div className="text-center">
              <p style={{ fontSize: 16, fontWeight: 300, color: 'var(--orbis-t1)', marginBottom: 6 }}>No domains yet</p>
              <p style={{ fontSize: 13, color: 'var(--orbis-t3)', maxWidth: 320, lineHeight: 1.6 }}>
                Register a new domain or connect one you already own. Starts at $12.99/year.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowConnect(v => !v)}
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t2)', border: '1px solid var(--orbis-border-md)' }}>
                Connect existing
              </button>
              <button onClick={() => setShowSearch(true)}
                className="orbis-cta px-5 py-2 rounded-full text-sm transition-all">
                Register a domain
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Table header */}
            <div className="hidden md:grid px-5 pb-1 text-xs font-semibold"
              style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr auto', color: 'var(--orbis-t4)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--orbis-mono)' }}>
              <span>Domain</span>
              <span>Status</span>
              <span>Project</span>
              <span>Auto-renew</span>
              <span></span>
            </div>
            {domains.map(d => (
              <DomainRow key={d.id} domain={d} projects={projects} onRefresh={load} />
            ))}
          </div>
        )}

        {/* Pricing note */}
        <p className="text-center pt-4" style={{ fontSize: 11, color: 'var(--orbis-t4)', lineHeight: 1.5 }}>
          Domains registered via Namecheap · at-cost pricing · WHOIS privacy included · auto-renews annually
        </p>
      </div>

      {/* Domain search modal */}
      <DomainSearchModal
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        onRegistered={() => { load(); setShowSearch(false); }}
      />
    </div>
  );
}
